import { useState } from 'react';
import classNames from 'classnames/bind';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './ActivityPanel.module.css';
import PanelContainer from './PanelContainer';
import PanelHeader from './PanelHeader';
import { useGetClassAttendancesQuery } from '../../../services/analysis';
import i18n from '../../../locales/i18n';
import { formatDate } from '../../../utils/date';
import {
  LAST_ATTENDANCE,
  NAME,
  SCHOOL_NAME,
  GRADE,
  GROUP,
  ATTENDED_ON_MOBILE,
  ATTENDED_ON_DESKTOP,
  STUDENTS_ATTENDED,
  NO_STUDENTS_ATTENDED,
} from '../../../constants';
import LoadingErrorPanel from './LoadingErrorPanel';
import { isMobile } from '../../../utils/util';
import parse from 'html-react-parser';

interface AttendanceStudent {
  userId: number;
  nm: string;
  schoolNm: string;
  gradeNm: string;
  groupNames: string;
}

interface ClassAttendancePanelProps {
  seq?: number[];
  attendedDates: string[];
  isOpen: boolean;
  onClose: () => void;
}

export default function ClassAttendancePanel({
  seq,
  attendedDates,
  isOpen,
  onClose,
}: ClassAttendancePanelProps) {
  const cx = classNames.bind(styles);

  const latestDate =
    attendedDates.length > 0 ? new Date(attendedDates[0]) : new Date();

  const [selectedDate, setSelectedDate] = useState<Date>(latestDate);

  const { data, isLoading, isError } = useGetClassAttendancesQuery({
    seq: seq?.[0],
    attendanceDate: formatDate(selectedDate, 1),
  });

  const highlightDates = attendedDates.map((dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  });

  const handleDateChange = (date: Date | null) => {
    if (date && attendedDates.includes(formatDate(date, 1))) {
      setSelectedDate(date);
    }
  };

  return (
    <PanelContainer isOpen={isOpen}>
      <PanelHeader
        cardId="last-date"
        title={LAST_ATTENDANCE}
        onClose={onClose}
      />
      <div className={cx('widget_content', 'atten', 'class')}>
        {isMobile || (
          <div className={cx('atten_calendar')}>
            <DatePicker
              inline
              selected={selectedDate}
              onChange={handleDateChange}
              locale={i18n.language}
              dateFormat={
                i18n.language === 'ko-KR' ? 'yyyy.MM.dd' : 'MM/dd/yyyy'
              }
              highlightDates={highlightDates}
              maxDate={new Date()}
              showDisabledMonthNavigation
              filterDate={(date) => attendedDates.includes(formatDate(date, 1))}
            />
          </div>
        )}

        <div className={cx('atten_info')}>
          {isLoading ? (
            <LoadingErrorPanel isLoading={true} />
          ) : isError || !data ? (
            <LoadingErrorPanel isError={true} />
          ) : (
            <>
              <div className={cx('atten_info_desc')}>
                <p>
                  <span className={cx('date', { strong_text: isMobile })}>
                    {formatDate(data.attendanceDate, 5)}
                  </span>
                  {parse(isMobile ? ATTENDED_ON_MOBILE : ATTENDED_ON_DESKTOP)}{' '}
                  <span className={cx({ strong_text: !isMobile })}>
                    {data.studentCount}
                  </span>
                  {STUDENTS_ATTENDED}
                </p>
              </div>

              {isMobile || (
                <div className={cx('table_container')}>
                  <div
                    className={cx('table_header', {
                      dynamic_right_padding:
                        data.studentList && data.studentList.length > 5,
                    })}
                  >
                    <div>{NAME}</div>
                    <div>{SCHOOL_NAME}</div>
                    <div>{GRADE}</div>
                    <div>{GROUP}</div>
                  </div>
                  <div className={cx('table_body')}>
                    {data.studentList && data.studentList.length > 0 ? (
                      data.studentList.map((student: AttendanceStudent) => (
                        <div key={student.userId} className={cx('table_row')}>
                          <div>{student.nm}</div>
                          <div>{student.schoolNm}</div>
                          <div>{student.gradeNm}</div>
                          <div>{student.groupNames}</div>
                        </div>
                      ))
                    ) : (
                      <div className={cx('empty_message')}>
                        {NO_STUDENTS_ATTENDED}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PanelContainer>
  );
}
